import { h, Component } from 'preact';
import { joinClasses } from 'utils/utils';
import { clamp } from 'utils/math';

import s from 'components/sortable/sortable.sss';

const preventDefault = e => e.preventDefault();
// Preact does not support passing "passive" to event listeners
// We need to specifically do this to prevent scrolling while dragging - pointerdown and/or not specifying passive:false does not work
const preventTouchStart = e => e?.addEventListener('touchstart', preventDefault, { passive: false });

const SCROLL_ZONE = 50; // pixels from edge to trigger scrolling
const SCROLL_SPEED = 5; // pixels per frame

export class Sortable extends Component {
	constructor(props) {
		super(props);
		this.onDragStart = this.onDragStart.bind(this);
		this.onDragMove = this.onDragMove.bind(this);
		this.onDragEnd = this.onDragEnd.bind(this);
		this.scrollLoop = this.scrollLoop.bind(this);
	}

	componentDidMount() {
		document.addEventListener('pointermove', this.onDragMove);
		document.addEventListener('pointerup', this.onDragEnd);
	}

	componentWillUnmount() {
		document.removeEventListener('pointermove', this.onDragMove);
		document.removeEventListener('pointerup', this.onDragEnd);
		if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
	}

	scrollLoop() {
		if (!this.dragged || !this.scrollDirection) {
			this.scrollFrame = null;
			return;
		}

		const horizontal = this.props.horizontal;
		const container = this.base;
		
		if (horizontal) {
			container.scrollLeft += this.scrollDirection * SCROLL_SPEED;
		} else {
			container.scrollTop += this.scrollDirection * SCROLL_SPEED;
		}

		// Update dragged element position to account for scroll
		this.updateDragPosition();

		this.scrollFrame = requestAnimationFrame(this.scrollLoop);
	}

	updateDragPosition() {
		if (!this.dragged || !this.dragged.pastSlop) return;
		
		const horizontal = this.props.horizontal;
		const { at, element, min, max, breakpoints, size, gap, startScroll } = this.dragged;
		const currentScroll = horizontal ? this.base.scrollLeft : this.base.scrollTop;
		const scrollDelta = currentScroll - startScroll;
		
		const position = this.lastPointerPos ?? at;
		const offset = clamp(position - at + scrollDelta, min, max);
		element.style.translate = horizontal ? `${offset}px 0` : `0 ${offset}px`;

		breakpoints.forEach(({ at, element, after }) => {
			let siblingOffset = 0;
			if (after ? offset > at : offset < at) {
				siblingOffset = (after ? -1 : 1) * (size + gap);
			}
			element.style.translate = horizontal ? `${siblingOffset}px 0` : `0 ${siblingOffset}px`;
		});
	}

	onDragStart(e) {
		if (this.props.disabled || e.pointerType === 'mouse' && e.button !== 0) {
			// Ignore non-primary button clicks (e.g. right clicks)
			return;
		}

		if (e.target.closest(`.${s.wrapper}`) !== this.base || !this.base.contains(e.target.closest(`.${s.grabbable}`))) {
			return;
		}

		const element = e.target.closest(`.${s.item}`);
		if (!element) return;
		e.preventDefault();

		const horizontal = this.props.horizontal;

		const mainBox = element.getBoundingClientRect();
		const gap = parseFloat(getComputedStyle(element.parentElement).gap) || 0;
		const siblings = [...element.parentElement.querySelectorAll(`:scope > .${s.item}`)];
		const startingIndex = siblings.indexOf(element);

		const size = horizontal ? mainBox.width : mainBox.height;

		let min = Infinity;
		let max = -Infinity;
		const breakpoints = siblings.map((el, i) => {
			const siblingBox = el.getBoundingClientRect();
			const start = horizontal ? siblingBox.left - mainBox.left : siblingBox.top - mainBox.top;
			const end = horizontal ? siblingBox.right - mainBox.right : siblingBox.bottom - mainBox.bottom;
			min = Math.min(min, start);
			max = Math.max(max, end);

			if (i === startingIndex) return null;

			const after = i > startingIndex;
			const elementSize = horizontal ? siblingBox.width : siblingBox.height;
			let at = start + elementSize / 2;
			if (after) at -= size;
			return { at, element: el, after, i };
		}).filter(e => e);

		const at = horizontal ? e.clientX : e.clientY;
		const startScroll = horizontal ? this.base.scrollLeft : this.base.scrollTop;
		const isScrollable = horizontal
			? this.base.scrollWidth > this.base.clientWidth
			: this.base.scrollHeight > this.base.clientHeight;
		this.dragged = { element, at, size, breakpoints, min, max, gap, startingIndex, pastSlop: false, startScroll, isScrollable };
	}

	onDragMove(e) {
		if (!this.dragged) return;
		const horizontal = this.props.horizontal;
		const { at, element } = this.dragged;

		const position = horizontal ? e.clientX : e.clientY;
		this.lastPointerPos = position;

		if (!this.dragged.pastSlop) {
			const slop = this.props.slop || 0;
			const dist = position - at;
			if (Math.abs(dist) < slop) {
				return;
			}
			this.dragged.pastSlop = true;
			element.setAttribute('dragging', '');
		}

		this.updateDragPosition();

		// Auto-scroll when near container edges (only if scrollable)
		if (!this.dragged.isScrollable) return;

		const container = this.base;
		const containerRect = container.getBoundingClientRect();
		const pointerPos = horizontal ? e.clientX : e.clientY;
		const containerStart = horizontal ? containerRect.left : containerRect.top;
		const containerEnd = horizontal ? containerRect.right : containerRect.bottom;

		let newScrollDirection = 0;
		if (pointerPos < containerStart + SCROLL_ZONE) {
			newScrollDirection = -1; // Scroll towards start
		} else if (pointerPos > containerEnd - SCROLL_ZONE) {
			newScrollDirection = 1; // Scroll towards end
		}

		if (newScrollDirection !== this.scrollDirection) {
			this.scrollDirection = newScrollDirection;
			if (newScrollDirection && !this.scrollFrame) {
				this.scrollFrame = requestAnimationFrame(this.scrollLoop);
			}
		}
	}

	onDragEnd(e) {
		// Stop auto-scrolling
		this.scrollDirection = 0;
		this.lastPointerPos = null;
		if (this.scrollFrame) {
			cancelAnimationFrame(this.scrollFrame);
			this.scrollFrame = null;
		}

		if (!this.dragged) return;
		if (!this.dragged.pastSlop) {
			this.dragged = null;
			return;
		}
		const { at, element, breakpoints, startingIndex, startScroll } = this.dragged;
		const horizontal = this.props.horizontal;

		const currentScroll = horizontal ? this.base.scrollLeft : this.base.scrollTop;
		const scrollDelta = currentScroll - startScroll;
		const position = horizontal ? e.clientX : e.clientY;
		const offset = position - at + scrollDelta;
		const from = element.getBoundingClientRect();

		let goalIndex = breakpoints.findIndex(({ at }) => offset < at);
		if (goalIndex === -1) goalIndex = breakpoints.length;
		if (goalIndex !== startingIndex) {
			this.props.onChange?.({ target: this.base, from: startingIndex, to: goalIndex, dragged: element });

			element.parentElement.insertBefore(element, breakpoints[goalIndex]?.element || breakpoints.at(-1).element.nextSibling);
		}

		breakpoints.forEach(({ element }) => element.style.translate = '');

		element.removeAttribute('dragging');
		element.style.translate = '';

		// animate smoothly to final position
		const to = element.getBoundingClientRect();
		element.style.translate = horizontal ? `${from.left - to.left}px 0` : `0 ${from.top - to.top}px`;
		element.clientWidth; // force reflow
		element.style.transition = 'translate .2s';
		element.style.translate = '';
		element.addEventListener('transitionend', () => element.style.transition = '', { once: true });

		this.dragged = null;
	}

	render(props) {
		const {
			horizontal,
			slop,
			children,
			onChange,
			disabled,
			...passThrough
		} = props;
		return (
			<div {...passThrough} class={joinClasses(s.wrapper, disabled && s.disabled, props.class)} onPointerDownCapture={this.onDragStart} ref={preventTouchStart}>
				{children}
			</div>
		);
	}
}

export class SortableItem extends Component {
	render({ grabbable, children, ...props }) {
		return (
			<div {...props} class={joinClasses(s.item, grabbable && s.grabbable, props.class)}>{children}</div>
		);
	}
}

export class Handle extends Component {
	// if you pass in a child component into this component, it will be used as the handle instead of the regular handle
	render(props) {
		return <div class={joinClasses(!props.children.length && s.handle, s.grabbable, props.class)}>{props.children}</div>;
	}
}